/**
 * Browser side of Web Push: permission, service worker, VAPID subscribe.
 */

import { ApiError, api } from "./api.ts";

export const PUSH_DENIED_HINT =
  "This browser has blocked alerts for eConnect DM. Open this site’s settings from the address bar, set Notifications to Allow, then reload this page.";

const FCM_ABORT_HINT =
  "This Chrome profile could not register for push. Reset site data for this origin, turn off blockers, or try another profile.";

export function isIosDevice() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export function isStandaloneDisplay() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  );
}

function urlBase64ToUint8Array(base64: string) {
  const padded =
    base64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
}

async function askClaim(registration: ServiceWorkerRegistration) {
  const worker = registration.active;
  if (!worker) return;
  if (navigator.serviceWorker.controller) return;
  await new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, 400);
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
    worker.postMessage("claim");
  });
}

function isPushAbort(error: unknown) {
  return (
    error instanceof Error &&
    (error.name === "AbortError" || /push service error/i.test(error.message))
  );
}

export async function requirePushRegistration() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service workers are not available here.");
  }
  let regs = await navigator.serviceWorker.getRegistrations();
  if (regs.length === 0) {
    await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    regs = await navigator.serviceWorker.getRegistrations();
    if (regs.length === 0) throw new Error("Register the service worker first.");
  }
  const registration = await navigator.serviceWorker.ready;
  await askClaim(registration);
  return registration;
}

export async function enableWebPush(): Promise<{ host: string }> {
  if (!("Notification" in window)) throw new Error("Notifications are not available here.");
  if (Notification.permission === "denied") throw new Error(PUSH_DENIED_HINT);
  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error(
        permission === "denied" ? PUSH_DENIED_HINT : "Notifications were not allowed.",
      );
    }
  }

  let publicKey: string;
  try {
    const vapid = await api.get<{ publicKey: string }>("/push/vapid");
    publicKey = vapid.publicKey;
  } catch (error) {
    throw new Error(error instanceof ApiError ? error.message : "Couldn’t load the push key");
  }

  const registration = await requirePushRegistration();
  if (!registration.pushManager) {
    throw new Error(
      "Push is missing. On iPhone, add eConnect to the Home Screen and open it from there.",
    );
  }

  const options = {
    userVisibleOnly: true as const,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  };
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe(options);
    } catch (error) {
      if (!isPushAbort(error)) throw error;
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
      await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      const fresh = await requirePushRegistration();
      try {
        subscription = await fresh.pushManager.subscribe(options);
      } catch (retryError) {
        if (isPushAbort(retryError)) throw new Error(FCM_ABORT_HINT);
        throw retryError;
      }
    }
  }

  try {
    await api.post("/push/subscribe", subscription.toJSON());
  } catch (error) {
    throw new Error(error instanceof ApiError ? error.message : "Couldn’t save the subscription");
  }
  return { host: new URL(subscription.endpoint).host };
}

/** True only when this browser has a live Web Push subscription. */
export async function thisDeviceCanPush() {
  try {
    if (!("Notification" in window)) return false;
    if (isIosDevice() && !isStandaloneDisplay()) return false;
    if (Notification.permission !== "granted") return false;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const registration of regs) {
      if (!registration.pushManager) continue;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function pushOptInCanAllow(homeScreen: boolean) {
  return !homeScreen;
}
